import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Car,
  Users,
  DollarSign,
  Settings,
  X,
  UserCog,
  User,
  ChevronDown,
  ChevronRight,
  FileText,
  Receipt,
  Calendar,
  CreditCard,
  Percent,
  LayoutGrid,
  Truck,
  HeartHandshake,
  Handshake,
  Workflow,
  Package,
  Wrench,
  Shield,
  MessageSquare,
  Store,
  ShoppingCart,
  FileSpreadsheet,
  Target,
} from "lucide-react";
import { cn } from "../../utils/cn";
import { FINANCIAL_NAV_ROUTES, isFinancialSubRouteActive } from "./financialNavRoutes";
import { SALES_NAV_ROUTES } from "./salesNavRoutes";
import { useAccessControl } from "../../contexts/AccessControlContext";
import { filterNavItems } from "../../utils/accessControl";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  subItems?: NavItem[];
  matchNested?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const FINANCIAL_SUB_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "/financial": DollarSign,
  "/financial/charges": DollarSign,
  "/financial/benefits": DollarSign,
  "/financial/invoices": Receipt,
  "/financial/payments": CreditCard,
  "/financial/commissions": Percent,
  "/financial/commission-rules": Receipt,
  "/financial/closures": Calendar,
  "/financial/fiscal-documents": FileText,
};

const SALES_SUB_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "/financial/sales": Workflow,
  "/proposals": FileText,
};

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccess, isReady } = useAccessControl();
  const [expandedItems, setExpandedItems] = useState<string[]>([
    "/leads",
    "/financial",
    "/sales",
    "/post-sales",
    "/settings",
    "/marketplace",
  ]);

  const sections: NavSection[] = useMemo(() => {
    const financialSubItems: NavItem[] = FINANCIAL_NAV_ROUTES.map((r) => ({
      name: t(`nav.financialSub.${r.labelKey}`),
      href: r.href,
      icon: FINANCIAL_SUB_ICONS[r.href] ?? DollarSign,
      matchNested: r.matchNested,
    }));

    const salesSubItems: NavItem[] = SALES_NAV_ROUTES.map((r) => ({
      name: t(`nav.salesSub.${r.labelKey}`),
      href: r.href,
      icon: SALES_SUB_ICONS[r.href] ?? Workflow,
      matchNested: r.matchNested,
    }));

  return [
   {
    label: "Menu",
    items: [
      { name: t("nav.dashboard"), href: "/", icon: LayoutDashboard },
      { name: t("nav.vehicles"), href: "/vehicles", icon: Car },
      {
        name: t("nav.leads"),
        href: "/leads",
        icon: Users,
        subItems: [
          { name: t("nav.leadsSub.all"), href: "/leads", icon: Users },
          { name: t("nav.leadsSub.report"), href: "/leads/report", icon: FileSpreadsheet },
          { name: t("nav.leadsSub.conversations"), href: "/leads/conversations", icon: MessageSquare },
          { name: t("nav.leadsSub.distribution"), href: "/leads/settings/distribution", icon: Target },
          { name: t("nav.leadsSub.settings"), href: "/leads/settings", icon: Settings },
        ],
      },
      { name: t("nav.sales"), href: "/proposals", icon: FileText },
      {
        name: t("nav.marketplace"),
        href: "/marketplace",
        icon: Store,
        subItems: [
          { name: t("nav.marketplaceSub.integrations"), href: "/marketplace/integrations", icon: Store },
          { name: t("nav.marketplaceSub.publications"), href: "/marketplace/publications", icon: ShoppingCart },
          { name: t("nav.marketplaceSub.history"), href: "/marketplace/history", icon: FileSpreadsheet },
        ],
      },
    ],
   },
   {
    label: t("nav.financial"),
    items: [
     {
      name: t("nav.financial"),
      href: "/financial",
      icon: DollarSign,
      subItems: financialSubItems,
     },
    ],
   },
   {
    label: t("nav.sales"),
    items: [
     {
      name: t("nav.sales"),
      href: "/sales",
      icon: Workflow,
      subItems: salesSubItems,
     },
    ],
   },
   {
    label: t("nav.postSales"),
    items: [
     {
      name: t("nav.postSales"),
      href: "/post-sales",
      icon: Handshake,
      subItems: [
       {
        name: t("nav.postSalesSub.dashboard"),
        href: "/post-sales/dashboard",
        icon: LayoutDashboard,
       },
       {
        name: t("nav.postSalesSub.salesPipeline"),
        href: "/post-sales/pipeline",
        icon: Workflow,
       },
       {
        name: t("nav.postSalesSub.preparationPipeline"),
        href: "/operation?tab=preparation",
        icon: LayoutGrid,
       },
       {
        name: t("nav.postSalesSub.deliveries"),
        href: "/operation?tab=deliveries",
        icon: Truck,
       },
       {
        name: t("nav.postSalesSub.postReceipt"),
        href: "/operation?tab=receipt",
        icon: HeartHandshake,
       },
       {
        name: "Produtos/Serviços",
        href: "/products",
        icon: Package,
       },
      ],
     },
    ],
   },
   {
    label: "Gestão",
    items: [
     { name: "Manutenção", href: "/maintenance", icon: Wrench },
     { name: "Usuários", href: "/users", icon: UserCog },
     {
      name: t("nav.settings"),
      href: "/settings",
      icon: Settings,
      subItems: [
       { name: "Geral", href: "/settings", icon: Settings },
       { name: "Tema", href: "/settings/theme", icon: Settings },
       { name: "Concessionária", href: "/settings/dealership", icon: Settings },
       { name: t("nav.settingsSub.billing"), href: "/settings/billing", icon: CreditCard },
       { name: "Formas de Pagamento", href: "/settings/payment-forms", icon: CreditCard },
       { name: t("nav.settingsSub.operation"), href: "/settings/operation", icon: Settings },
       { name: t("nav.settingsSub.leads"), href: "/settings/leads", icon: Settings },
       { name: t("nav.settingsSub.vehicleEvaluation"), href: "/settings/vehicle-evaluation", icon: Settings },
       { name: t("nav.settingsSub.roleAccess"), href: "/settings/role-access", icon: Shield },
      ],
     },
    ],
   },
  ];
 }, [t]);

  const filteredSections = useMemo(
    () =>
      sections
        .map(section => ({
          ...section,
          items: filterNavItems(section.items, canAccess),
        }))
        .filter(section => section.items.length > 0),
    [sections, canAccess],
  );

  useEffect(() => {
    const p = location.pathname;
    setExpandedItems((prev) => {
      const keysToAdd: string[] = [];
      const need = (key: string, cond: boolean) => {
        if (cond && !keysToAdd.includes(key)) keysToAdd.push(key);
      };
      need("/financial", p.startsWith("/financial"));
      need("/leads", p.startsWith("/leads"));
      need("/sales", p.startsWith("/financial/sales") || p.startsWith("/proposals"));
      need("/post-sales", p.startsWith("/post-sales") || p.startsWith("/operation/") || p.startsWith("/settings/operation-pipeline"));
      need("/settings", p.startsWith("/settings") || p.startsWith("/rbac"));
      need("/marketplace", p.startsWith("/marketplace"));
      let next = prev;
      for (const key of keysToAdd) {
        if (!next.includes(key)) next = [...next, key];
      }
      return next;
    });
  }, [location.pathname]);

  const toggleExpand = (key: string) => {
    setExpandedItems((prev) =>
      prev.includes(key) ? prev.filter((n) => n !== key) : [...prev, key]
    );
  };

  const isItemActive = (item: NavItem) => {
    if (item.subItems) {
      if (item.href === "/financial") {
        return item.subItems.some((sub) =>
          isFinancialSubRouteActive(location.pathname, sub.href, sub.matchNested)
        );
      }
      if (item.href === "/sales") {
        return item.subItems.some(
          (sub) =>
            location.pathname === sub.href ||
            (!!sub.matchNested && location.pathname.startsWith(`${sub.href}/`))
        );
      }
      if (item.href === "/post-sales") {
        return item.subItems.some(
          (sub) =>
            location.pathname === sub.href || location.pathname.startsWith(`${sub.href}/`)
        );
      }
      if (item.href === "/settings") {
        return item.subItems.some(
          (sub) =>
            location.pathname === sub.href || location.pathname.startsWith(`${sub.href}/`)
        );
      }
      if (item.href === "/marketplace") {
        return item.subItems.some(
          (sub) =>
            location.pathname === sub.href || location.pathname.startsWith(`${sub.href}/`)
        );
      }
      return item.subItems.some((sub) => location.pathname === sub.href);
    }
    return location.pathname === item.href;
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onToggle}
        />
      )}

      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-60 flex flex-col",
          "bg-card dark:bg-primary-900",
          "border-r border-border",
          "transform transition-transform duration-200 ease-out",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-14 items-center justify-between px-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="ambcar" className="h-7 w-auto object-contain flex-shrink-0" />
            <span
              className="text-sm font-bold text-primary-900 dark:text-white tracking-tight"
              style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
            >
              ambcar
            </span>
          </div>
          <button
            type="button"
            onClick={onToggle}
            className="lg:hidden rounded-md p-1 text-accent hover:text-primary-700 hover:bg-accent/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
          {!isReady ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-accent rounded-full border-t-transparent" />
            </div>
          ) : (
          filteredSections.map((section) => (
            <div key={section.label}>
              {!(section.items.length === 1 && section.items[0].name === section.label) && (
                <p className="sidebar-section-label">{section.label}</p>
              )}

              {section.items.map((item) => {
                const isActive = isItemActive(item);
                const expandKey = item.subItems ? item.href : item.name;
                const isExpanded = item.subItems ? expandedItems.includes(expandKey) : false;

                return (
                  <div key={`${section.label}-${item.href}`}>
                    <button
                      type="button"
                      onClick={() => {
                        if (item.subItems) {
                          toggleExpand(expandKey);
                        } else {
                          navigate(item.href);
                          if (window.innerWidth < 1024) onToggle();
                        }
                      }}
                      className={cn(
                        "sidebar-item justify-between",
                        isActive ? "sidebar-item-active" : "sidebar-item-inactive"
                      )}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-accent-600 rounded-r-full" />
                      )}

                      <div className="flex items-center gap-2.5">
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.name}</span>
                      </div>

                      {item.subItems && (
                        isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                          : <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                      )}
                    </button>

                    {item.subItems && isExpanded && (
                      <div className="ml-7 mt-0.5 space-y-0.5">
                        {item.subItems.map((subItem) => {
                          const isSubActive =
                            item.href === "/financial"
                            ? isFinancialSubRouteActive(location.pathname, subItem.href, subItem.matchNested)
                            : item.href === "/sales"
                            ? location.pathname === subItem.href ||
                              (!!subItem.matchNested && location.pathname.startsWith(`${subItem.href}/`))
                            : item.href === "/post-sales"
                            ? location.pathname === subItem.href || location.pathname.startsWith(`${subItem.href}/`)
                            : item.href === "/settings"
                            ? location.pathname === subItem.href || location.pathname.startsWith(`${subItem.href}/`)
                            : item.href === "/marketplace"
                            ? location.pathname === subItem.href || location.pathname.startsWith(`${subItem.href}/`)
                            : location.pathname === subItem.href;
                          return (
                            <button
                              type="button"
                              key={subItem.href}
                              onClick={() => {
                                navigate(subItem.href);
                                if (window.innerWidth < 1024) onToggle();
                              }}
                              className={cn(
                                "sidebar-subitem",
                                isSubActive ? "sidebar-subitem-active" : "sidebar-subitem-inactive"
                              )}
                            >
                              {subItem.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )))}
        </nav>
      </div>
    </>
  );
};